import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider,
  Alert,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon
} from '@mui/icons-material';

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Dropdown' },
  { value: 'boolean', label: 'Yes/No' },
  { value: 'textarea', label: 'Long Text' }
];

const PersonTypeDialog = ({ open, onClose, onSave, personType, saving }) => {
  const [form, setForm] = useState({
    friendlyName: '',
    dataModel: []
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (personType) {
      setForm({
        friendlyName: personType.friendlyName || '',
        dataModel: personType.dataModel || []
      });
    } else {
      setForm({
        friendlyName: '',
        dataModel: []
      });
    }
    setErrors({});
  }, [personType, open]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const addField = () => {
    setForm(prev => ({
      ...prev,
      dataModel: [
        ...prev.dataModel,
        {
          key: '',
          label: '',
          type: 'text',
          required: false,
          options: []
        }
      ]
    }));
  };

  const updateField = (index, updates) => {
    setForm(prev => ({
      ...prev,
      dataModel: prev.dataModel.map((field, i) =>
        i === index ? { ...field, ...updates } : field
      )
    }));
  };

  const removeField = (index) => {
    setForm(prev => ({
      ...prev,
      dataModel: prev.dataModel.filter((_, i) => i !== index)
    }));
  };

  const updateFieldOptions = (index, optionsText) => {
    const options = optionsText.split(',').map(o => o.trim()).filter(o => o);
    updateField(index, { options });
  };

  const validate = () => {
    const newErrors = {};
    if (!form.friendlyName.trim()) {
      newErrors.friendlyName = 'Name is required';
    }

    // Validate dataModel fields
    form.dataModel.forEach((field, index) => {
      if (!field.key.trim()) {
        newErrors[`field_${index}_key`] = 'Field key is required';
      }
      if (!field.label.trim()) {
        newErrors[`field_${index}_label`] = 'Field label is required';
      }
      if (field.type === 'select' && (!field.options || field.options.length === 0)) {
        newErrors[`field_${index}_options`] = 'Dropdown options are required';
      }
    });

    // Check for duplicate keys
    const keys = form.dataModel.map(f => f.key.toLowerCase());
    const duplicates = keys.filter((key, index) => keys.indexOf(key) !== index);
    if (duplicates.length > 0) {
      newErrors.duplicateKeys = `Duplicate field keys: ${[...new Set(duplicates)].join(', ')}`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      // Generate keys from labels if not provided
      const processedDataModel = form.dataModel.map(field => ({
        ...field,
        key: field.key || field.label.toLowerCase().replace(/\s+/g, '_')
      }));

      onSave({
        friendlyName: form.friendlyName.trim(),
        dataModel: processedDataModel
      });
    }
  };

  const isEditing = !!personType;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEditing ? 'Edit Person Type' : 'Add Person Type'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <TextField
            fullWidth
            label="Name"
            value={form.friendlyName}
            onChange={(e) => handleChange('friendlyName', e.target.value)}
            error={!!errors.friendlyName}
            helperText={errors.friendlyName}
            required
            sx={{ mb: 3 }}
          />

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="medium">
              Custom Fields
            </Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={addField}
              size="small"
              variant="outlined"
            >
              Add Field
            </Button>
          </Box>

          {errors.duplicateKeys && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.duplicateKeys}
            </Alert>
          )}

          {form.dataModel.length === 0 ? (
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                textAlign: 'center',
                bgcolor: 'grey.50',
                borderStyle: 'dashed'
              }}
            >
              <Typography color="text.secondary">
                No custom fields defined. Click "Add Field" to create fields for this person type.
              </Typography>
            </Paper>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {form.dataModel.map((field, index) => (
                <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <DragIcon sx={{ color: 'text.disabled', mt: 2 }} />

                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                          label="Label"
                          value={field.label}
                          onChange={(e) => updateField(index, { label: e.target.value })}
                          error={!!errors[`field_${index}_label`]}
                          helperText={errors[`field_${index}_label`]}
                          size="small"
                          sx={{ flex: 1 }}
                        />
                        <TextField
                          label="Key"
                          value={field.key}
                          onChange={(e) => updateField(index, { key: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                          error={!!errors[`field_${index}_key`]}
                          helperText={errors[`field_${index}_key`] || 'Auto-generated from label'}
                          size="small"
                          sx={{ flex: 1 }}
                        />
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <InputLabel>Type</InputLabel>
                          <Select
                            value={field.type}
                            onChange={(e) => updateField(index, { type: e.target.value })}
                            label="Type"
                          >
                            {FIELD_TYPES.map(type => (
                              <MenuItem key={type.value} value={type.value}>
                                {type.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>

                      {field.type === 'select' && (
                        <TextField
                          label="Options (comma-separated)"
                          value={(field.options || []).join(', ')}
                          onChange={(e) => updateFieldOptions(index, e.target.value)}
                          error={!!errors[`field_${index}_options`]}
                          helperText={errors[`field_${index}_options`] || 'e.g., Option 1, Option 2, Option 3'}
                          size="small"
                          fullWidth
                        />
                      )}

                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={field.required || false}
                              onChange={(e) => updateField(index, { required: e.target.checked })}
                              size="small"
                            />
                          }
                          label="Required"
                        />
                        {field.type === 'text' && (
                          <Chip
                            label={field.key}
                            size="small"
                            variant="outlined"
                            color="default"
                          />
                        )}
                      </Box>
                    </Box>

                    <IconButton
                      onClick={() => removeField(index)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving}
        >
          {saving ? 'Saving...' : isEditing ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PersonTypeDialog;
